import { useMemo } from 'react';
import useSWR from 'swr';
import { swrKeys } from '@shared/api/swr/keys';
import { swrPaginatedListConfig } from '@shared/api/swr/list-config';
import { isActiveWithdrawalState, mapAdminWithdrawalDtoToEntity, mapWithdrawalDtoToEntity } from '@entities/withdrawal';
import type { WithdrawalAdminDto, WithdrawalUserDto } from '@shared/api/contracts';

const DEFAULT_LIMIT = 20;

const toPaginationNumber = (value: unknown, fallback: number) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

export const useWithdrawalsList = (page = 1, limit = DEFAULT_LIMIT, isAdmin = false, state = '') => {
  const params = { page, limit, ...(state ? { state } : {}) };
  const swrKey = isAdmin
    ? swrKeys.withdrawals.admin(params)
    : swrKeys.withdrawals.all(params);

  const { data, error, isLoading, isValidating, mutate } = useSWR(swrKey, {
    ...swrPaginatedListConfig,
    refreshInterval: (latest) => {
      const items = (latest as { data?: Array<{ state?: string | null; status?: string }> } | undefined)?.data ?? [];
      return items.some((item) => isActiveWithdrawalState(item.state, item.status)) ? 10000 : 0;
    },
  });

  const withdrawals = useMemo(() => {
    const items = data?.data ?? [];

    if (isAdmin) {
      return (items as WithdrawalAdminDto[]).map(mapAdminWithdrawalDtoToEntity);
    }

    return (items as WithdrawalUserDto[]).map(mapWithdrawalDtoToEntity);
  }, [data?.data, isAdmin]);

  const pagination = data?.pagination
    ? {
        page: toPaginationNumber(data.pagination.page, page),
        limit: toPaginationNumber(data.pagination.limit, limit),
        total: toPaginationNumber(data.pagination.total, 0),
      }
    : { page, limit, total: 0 };

  return {
    withdrawals,
    pagination,
    isLoading: isLoading && !data,
    isValidating,
    error,
    mutate,
  };
};
